import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import { Icon } from "./Icon";
import type { BackLinkProps } from "../types";

/**
 * H2's own "Back link" (README.md "Global shell": "above the card:
 * `chevronLeft` icon + underlined text, 17px `#0f3253`, 44px target" —
 * e.g. `06-Declaration.dc.html:58`'s `backLink` style,
 * `04-05-GuidedExposures.dc.html:111-115`'s own back buttons). Renders a
 * native `button` styled as an underlined text link, never the adapter's
 * own `Button` (whose chrome and `fullWidth` stretch do not match H2's
 * plain inline link treatment here).
 *
 * Not generic upstream (a plain native `button` plus `Box`, like
 * `PortalPanel`'s own trigger) — no containment note beyond "no Material
 * UI type crosses the boundary" applies.
 *
 * Generic and screen-agnostic by design: every H2 screen's own back
 * link differs only in its destination and its visible label, so this
 * component takes both as plain props rather than naming "exposures" or
 * "sections" anywhere — `DeclarationRoute` and `SectionPageRoute` are its
 * first two callers, and a third (`SectionListRoute`'s own "Back to
 * confirm details" link) is tracked for a later wave.
 */
export function BackLink({ label, onClick, testId, compact }: Readonly<BackLinkProps>) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      {...cleanProps({ "data-testid": testId })}
      sx={{
        appearance: "none",
        background: "none",
        border: "none",
        // ADR-20261102-h2fd (AC-14): H2's back link, 10px above and below.
        padding: compact === true ? `${tokens.spacing.xs}px 0` : "10px 0",
        margin: 0,
        minHeight: tokens.interactionState.targetSize.standard,
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: `${tokens.spacing.xs}px`,
        fontFamily: "inherit",
        fontSize: tokens.typography.variants.body.size,
        color: tokens.colour.brand[700],
        textDecoration: "underline",
        cursor: "pointer",
        "&:focus-visible": {
          outline: `${tokens.interactionState.focusRing.width}px solid ${tokens.interactionState.focusRing.colourOnLight}`,
          outlineOffset: tokens.interactionState.focusRing.offset,
        },
      }}
    >
      {/* H2: Icon name="chevronLeft" size="md" tone="muted". */}
      <Icon name="chevronLeft" size={22} tone="muted" />
      {label}
    </Box>
  );
}
